/* `?worker&url` for the reason given in `createPcmCapture`. */
import playbackWorkletUrl from './realtimeVoice.playback.worklet.ts?worker&url';
import { createPcmCapture, PCM_SAMPLE_RATE } from './createPcmCapture.ts';
import { openChosenMicrophone } from './openChosenMicrophone.ts';
import type {
  RealtimeCaptureMessage,
  RealtimePlaybackCommand,
  RealtimePlaybackReport,
} from './realtimeAudioMessages.ts';
import { voiceMicrophoneConstraints } from './voiceMicrophoneConstraints.ts';

/**
 * Chrome can send a whole AudioContext to a chosen speaker. The DOM lib has no
 * word for it yet, and the browsers that cannot do it simply do not have it.
 */
interface SinkSelectableAudioContext extends AudioContext {
  setSinkId?: (deviceId: string) => Promise<void>;
}

export interface RealtimeAudioGraphOptions {
  microphoneId: string | null;
  speakerId: string | null;
  onCapture: (message: RealtimeCaptureMessage) => void;
  onPlayback: (report: RealtimePlaybackReport) => void;
}

/**
 * The microphone and the speaker of a spoken practice conversation: the
 * microphone goes into the capture worklet, the character's voice comes out of
 * the playback worklet. Set up once per call; a reconnecting socket keeps it.
 */
export class RealtimeAudioGraph {
  private readonly onCapture: (message: RealtimeCaptureMessage) => void;
  private readonly onPlayback: (report: RealtimePlaybackReport) => void;

  private microphoneId: string | null;
  private speakerId: string | null;
  private microphoneOn = true;

  private context: SinkSelectableAudioContext | null = null;
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private capture: AudioWorkletNode | null = null;
  private playback: AudioWorkletNode | null = null;

  constructor(options: RealtimeAudioGraphOptions) {
    this.microphoneId = options.microphoneId;
    this.speakerId = options.speakerId;
    this.onCapture = options.onCapture;
    this.onPlayback = options.onPlayback;
  }

  /** Rejects when the microphone is refused. */
  async open(): Promise<void> {
    this.stream = await openChosenMicrophone(
      this.microphoneId,
      voiceMicrophoneConstraints,
    );

    /*
     * The AudioContext is made here rather than at construction because a
     * browser only lets a page make noise off a gesture, and the Start button
     * is the one this call has.
     */
    const context: SinkSelectableAudioContext = new AudioContext({
      sampleRate: PCM_SAMPLE_RATE,
    });
    this.context = context;

    await context.audioWorklet.addModule(playbackWorkletUrl);
    await context.resume();
    await this.playThroughSpeaker();

    this.playback = new AudioWorkletNode(context, 'realtime-playback', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    this.playback.port.onmessage = (event: MessageEvent<RealtimePlaybackReport>) => {
      this.onPlayback(event.data);
    };
    this.playback.connect(context.destination);

    this.capture = await createPcmCapture(context, (message) => this.onCapture(message));

    this.attachMicrophone();
  }

  /** Muted here rather than at the bridge: nothing should leave the machine. */
  setMicrophoneEnabled(enabled: boolean): void {
    this.microphoneOn = enabled;
    this.stream?.getAudioTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  /**
   * A device picked just now is asked for exactly: if it is gone, the one
   * already running stays rather than the default taking over unseen.
   */
  async setMicrophone(deviceId: string): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: voiceMicrophoneConstraints(deviceId),
    });
    this.microphoneId = deviceId;

    this.releaseMicrophone();
    this.stream = stream;
    this.setMicrophoneEnabled(this.microphoneOn);
    this.attachMicrophone();
  }

  async setSpeaker(deviceId: string): Promise<void> {
    this.speakerId = deviceId;
    await this.playThroughSpeaker();
  }

  /** Hands the buffer over to the audio thread; it is unusable here afterwards. */
  play(pcm: ArrayBuffer): void {
    this.playback?.port.postMessage(
      { type: 'audio', pcm: new Int16Array(pcm) } satisfies RealtimePlaybackCommand,
      [pcm],
    );
  }

  command(command: RealtimePlaybackCommand): void {
    this.playback?.port.postMessage(command);
  }

  /** Lets go of the microphone and the nodes at once. */
  release(): void {
    this.releaseMicrophone();
    this.source?.disconnect();
    this.capture?.disconnect();
    this.playback?.disconnect();
    this.source = null;
    this.capture = null;
    this.playback = null;
  }

  async close(): Promise<void> {
    const context = this.context;
    this.context = null;
    await context?.close().catch(() => undefined);
  }

  private attachMicrophone(): void {
    if (this.context === null || this.stream === null || this.capture === null) {
      return;
    }

    this.source?.disconnect();
    this.source = this.context.createMediaStreamSource(this.stream);
    this.source.connect(this.capture);
  }

  private releaseMicrophone(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }

  /**
   * Never fatal: a browser that cannot switch outputs still has a working call
   * on whatever it was already using.
   */
  private async playThroughSpeaker(): Promise<void> {
    const context = this.context;
    if (context === null || this.speakerId === null || context.setSinkId === undefined) {
      return;
    }

    try {
      await context.setSinkId(this.speakerId);
    } catch {
      /* The call keeps playing where it was. */
    }
  }
}
